<template>
  <div class="flex justify-center items-center w-full min-h-screen pb-10">
    <div
      class="max-w-xl w-full sm:rounded-lg sm:shadow-lg px-6 py-10
      flex flex-col items-center justify-center relative"
    >
      <IconArrowLeft
        class="self-start cursor-pointer text-blue-900"
        @click="switchToLoginPanel"
      />

      <Logo class="place-self-end self-center -mt-6 mb-2" />

      <p class="text-2xl mb-8 h-3">
        {{ forgotPasswordMessages.title }}
      </p>
      <p
        v-if="!forgotPasswordSuccess"
        class="text-base mb-8 sm:w-9/12"
      >
        {{ forgotPasswordMessages.description }}
      </p>

      <div class="flex-col justify-center w-80 space-y-4">
        <Input
          v-if="!forgotPasswordSuccess"
          v-model="email"
          type="email"
          name="email"
          autocomplete="email"
          autofocus
          :placeholder="$t('message.email_address')"
          :error="checkEmailError"
        />

        <Button
          v-if="!forgotPasswordSuccess"
          class="w-80"
          type="primary"
          :disabled="!enableSignInButton"
          @click="handleForgotPassword"
        >
          {{ forgotPasswordMessages.btnText }}
        </Button>
        <Message
          v-if="message && message.type && message.text"
          class="w-full"
          :type="message.type"
          :text="message.text"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { InputValidator } from '@/utils';
import Input from '@/components/atoms/Input.vue';
import Button from '@/components/atoms/Button.vue';
import Message from '@/components/atoms/Message.vue';
import Logo from '@/components/atoms/Logo.vue';
import IconArrowLeft from '@/components/atoms/IconArrowLeft.vue';
import { forgotPassword } from '@/service/cognitoService';
import { mapActions, mapGetters } from 'vuex';
import { AppStatus } from '@/model/status';
import { AlertMessage, MessageType } from '@/model/message';
import { CognitoException } from '@/model/cognito';

export default defineComponent({
  name: 'ForgotPassword',
  components: {
    Input,
    Button,
    Message,
    IconArrowLeft,
    Logo
  },
  data () {
    return {
      email: '' as string,
      message: {} as AlertMessage,
      forgotPasswordSuccess: false
    };
  },
  mounted () {
    if (this.myEmail) {
      this.email = this.myEmail;
      this.setMyEmail('');
    }
    if (this.appStatus === AppStatus.ResetPasswordRequired) {
      this.message.type = MessageType.Error;
      this.message.text = this.$t('message.reset_password_required');
    }
  },
  methods: {
    ...mapActions(['setMyEmail', 'setAppStatus', 'startLoading', 'endLoading']),

    async handleForgotPassword () {
      try {
        this.startLoading();
        await forgotPassword(this.email.toLowerCase());
        this.message.type = MessageType.Success;
        this.message.text = this.$t('message.forgot_password_success');
        this.forgotPasswordSuccess = true;
      } catch (error: any) {
        this.message.type = MessageType.Error;
        if (error.code === CognitoException.LimitExceededException) {
          this.message.text = this.$t('message.forgot_password_error');
        } else if (error.code === CognitoException.UserNotFoundException) {
          this.message.text = this.$t('message.forgot_password_try_social');
        } else {
          this.message.text = this.$t('message.forgot_password_generic_error');
        }
      } finally {
        this.endLoading();
        this.email = '';
      }
    },
    switchToLoginPanel (): void {
      this.$router.push({
        path: 'login',
        query: {
          ...this.$route.query
        }
      });
    }
  },
  computed: {
    ...mapGetters([
      'global',
      'forgotPasswordMessages',
      'myEmail',
      'appStatus'
    ]),
    checkEmailError (): boolean {
      return this.email ? !InputValidator.validateEmail(this.email) : false;
    },
    enableSignInButton (): boolean {
      return InputValidator.validateEmail(this.email);
    }
  }
});
</script>
